import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, CheckCircle2, ChevronLeft, ChevronRight, Clock, Loader2, Mail, Phone, User } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

const WEEKDAY_ABBR = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDayLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${WEEKDAY_ABBR[(d.getDay() + 6) % 7]} · ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function formatFullDateTime(iso) {
  const d = new Date(iso);
  return `${WEEKDAY_ABBR[(d.getDay() + 6) % 7]}, ${MONTHS[d.getMonth()]} ${d.getDate()} · ${formatTime(iso)}`;
}

/** Public appointment scheduler at /schedule */
export default function Schedule() {
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [purpose, setPurpose] = useState("");
  const [form, setForm] = useState({ client_name: "", email: "", phone: "", notes: "" });
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    document.title = "Book a Call · DW Investment Group";
    (async () => {
      try {
        const res = await fetch(`${API}/api/scheduler/availability`);
        if (!res.ok) throw new Error("Failed to load availability");
        const data = await res.json();
        setAvail(data);
        setPurpose(data.settings.purposes?.[0] || "Pre-approval consultation");
        const firstDay = (data.days || []).find((d) => d.slots.length > 0);
        if (firstDay) setSelectedDate(firstDay.date);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const daysWithSlots = useMemo(() => (avail?.days || []).filter((d) => d.slots.length > 0), [avail]);
  const activeDay = useMemo(
    () => (avail?.days || []).find((d) => d.date === selectedDate),
    [avail, selectedDate]
  );

  const book = async () => {
    if (!selectedSlot) return;
    if (!form.client_name || !form.email) {
      setError("Please provide your full name and email.");
      return;
    }
    setError("");
    setBooking(true);
    try {
      const res = await fetch(`${API}/api/scheduler/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: form.client_name,
          email: form.email,
          phone: form.phone || null,
          purpose,
          notes: form.notes || null,
          start_time_iso: selectedSlot.start,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Booking failed. Please try again.");
      setConfirmed(data.appointment);
    } catch (e) {
      setError(e.message);
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800/70 bg-slate-950/80 backdrop-blur">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.3em] text-emerald-300">DW INVESTMENT GROUP</span>
          </Link>
          <Link to="/" className="text-[11px] font-semibold text-slate-400 hover:text-emerald-300 transition">← Back to home</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Book a call</h1>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm">
            Pick a time that works for you. Confirmation and reminders are sent by email and text — no back-and-forth needed.
          </p>
        </div>

        {confirmed ? (
          <ConfirmationCard appt={confirmed} />
        ) : loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-emerald-400" size={28} />
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Day picker */}
            <div className="lg:col-span-2 bg-slate-900/60 rounded-2xl border border-slate-800 p-5">
              <h2 className="text-sm font-bold tracking-wide mb-4 flex items-center gap-2">
                <Calendar size={15} className="text-emerald-300" /> Pick a day
              </h2>
              <div className="flex flex-col gap-1.5 max-h-[520px] overflow-y-auto pr-1">
                {daysWithSlots.length === 0 ? (
                  <p className="text-xs text-slate-500">No availability in the next 30 days — please contact us directly.</p>
                ) : (
                  daysWithSlots.map((d) => {
                    const active = selectedDate === d.date;
                    return (
                      <button
                        key={d.date}
                        data-testid={`day-${d.date}`}
                        onClick={() => { setSelectedDate(d.date); setSelectedSlot(null); }}
                        className={`text-left px-3 py-2.5 rounded-lg border transition ${
                          active
                            ? "border-emerald-500 bg-emerald-500/10 text-emerald-200"
                            : "border-slate-800 hover:border-slate-600 text-slate-300"
                        }`}
                      >
                        <div className="text-sm font-semibold">{formatDayLabel(d.date)}</div>
                        <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 mt-0.5">
                          {d.slots.length} SLOT{d.slots.length === 1 ? "" : "S"}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Slot + form */}
            <div className="lg:col-span-3 bg-slate-900/60 rounded-2xl border border-slate-800 p-5">
              {!activeDay ? (
                <p className="text-sm text-slate-400">Select a day to see time slots.</p>
              ) : (
                <>
                  <h2 className="text-sm font-bold tracking-wide mb-3 flex items-center gap-2">
                    <Clock size={15} className="text-emerald-300" /> {formatDayLabel(activeDay.date)}
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
                    {activeDay.slots.map((s) => {
                      const active = selectedSlot?.start === s.start;
                      return (
                        <button
                          key={s.start}
                          data-testid={`slot-${s.start}`}
                          onClick={() => setSelectedSlot(s)}
                          className={`px-2 py-2 rounded-lg border text-xs font-semibold transition ${
                            active
                              ? "border-emerald-500 bg-emerald-500 text-black"
                              : "border-slate-700 hover:border-emerald-500 text-slate-300"
                          }`}
                        >
                          {formatTime(s.start)}
                        </button>
                      );
                    })}
                  </div>

                  <AnimatePresence>
                    {selectedSlot ? (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col gap-3"
                      >
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Field
                            testId="book-name"
                            icon={<User size={13} />}
                            label="Full name"
                            value={form.client_name}
                            onChange={(v) => setForm((f) => ({ ...f, client_name: v }))}
                          />
                          <Field
                            testId="book-email"
                            icon={<Mail size={13} />}
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                          />
                          <Field
                            testId="book-phone"
                            icon={<Phone size={13} />}
                            label="Phone (for SMS reminder)"
                            value={form.phone}
                            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                          />
                          <div>
                            <label className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase mb-1 block">Purpose</label>
                            <select
                              data-testid="book-purpose"
                              value={purpose}
                              onChange={(e) => setPurpose(e.target.value)}
                              className="w-full rounded-lg bg-slate-950 border border-slate-700 px-2.5 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500"
                            >
                              {(avail?.settings?.purposes || []).map((p) => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase mb-1 block">Anything we should know?</label>
                          <textarea
                            data-testid="book-notes"
                            rows={2}
                            value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            className="w-full rounded-lg bg-slate-950 border border-slate-700 px-2.5 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500"
                            placeholder="Loan goals, timeline, questions…"
                          />
                        </div>
                        {error ? <p className="text-xs text-rose-400">{error}</p> : null}
                        <button
                          data-testid="book-confirm"
                          onClick={book}
                          disabled={booking}
                          className="mt-1 w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-bold text-sm py-2.5 transition flex items-center justify-center gap-2"
                        >
                          {booking ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
                          Confirm {formatTime(selectedSlot.start)}
                        </button>
                      </motion.div>
                    ) : (
                      <p className="text-xs text-slate-500">Pick a time above to continue.</p>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase mb-1 flex items-center gap-1.5">
        {icon} {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-slate-950 border border-slate-700 px-2.5 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500"
      />
    </div>
  );
}

function ConfirmationCard({ appt }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-lg mx-auto bg-slate-900/70 border border-emerald-500/50 rounded-2xl p-8 text-center shadow-[0_0_60px_-15px_rgba(16,185,129,0.6)]"
      data-testid="book-confirmation"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500 flex items-center justify-center mb-4">
        <CheckCircle2 size={30} className="text-emerald-300" />
      </div>
      <h2 className="text-2xl font-bold">You&apos;re booked!</h2>
      <p className="text-sm text-slate-400 mt-2">
        {formatFullDateTime(appt.start_time)}
        <br />
        <span className="text-emerald-300 font-semibold">{appt.purpose}</span>
      </p>
      <p className="text-xs text-slate-500 mt-4">
        We&apos;ve sent a confirmation to {appt.email}. Reminders arrive 24 hours and 1 hour before we meet.
      </p>
      <Link
        to="/"
        className="inline-block mt-6 px-4 py-2 rounded-lg border border-slate-700 hover:border-emerald-500 text-slate-300 hover:text-emerald-300 text-xs font-semibold transition"
      >
        Back to home
      </Link>
    </motion.div>
  );
}
